import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { ArrowLeft, Save, Send, Archive } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EVENT_TYPES = ["SOCIAL", "TOURNAMENT", "CLINIC", "EXHIBITION", "COMMUNITY", "OTHER"];
const AUDIENCES = ["ALL_MEMBERS", "CHAPTER_ONLY", "FOUNDING_MEMBERS", "INVITED"];

const statusStyle = (s) => {
  if (s === "PUBLISHED") return "text-lv-active bg-lv-active/10 border-lv-active/20";
  if (s === "EXPIRED") return "text-lv-inactive bg-lv-inactive/10 border-lv-inactive/20";
  return "text-amber-400 bg-amber-400/10 border-amber-400/20";
};

export default function EventEdit() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: event, isLoading } = useQuery({ queryKey: ["admin-event", id], queryFn: () => api.get(`/admin/events/${id}`) });
  const { data: chapters = [] } = useQuery({ queryKey: ["chapters"], queryFn: () => api.get("/chapters") });

  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event && !form) {
      setForm({
        title: event.title || "", description: event.description || "",
        event_type: event.event_type || "SOCIAL", date: event.date || "",
        time_start: event.time_start || "", time_end: event.time_end || "",
        location: event.location || "", chapter_id: event.chapter_id || "",
        audience: event.audience || "ALL_MEMBERS", image_url: event.image_url || "",
        member_price: event.member_price || "", capacity: event.capacity ?? "",
        action_url: event.action_url || "", action_label: event.action_label || "",
        publish_date: event.publish_date || "", expiration_date: event.expiration_date || "",
      });
    }
  }, [event, form]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-event", id] }); qc.invalidateQueries({ queryKey: ["admin-events"] }); };

  const saveEvent = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/events/${id}`, {
        ...form,
        chapter_id: form.chapter_id || null,
        image_url: form.image_url || null,
        capacity: form.capacity ? parseInt(form.capacity) : null,
        member_price: form.member_price || null,
        action_url: form.action_url || null,
        action_label: form.action_label || null,
        publish_date: form.publish_date || null,
        expiration_date: form.expiration_date || null,
      });
      toast.success("Event updated");
      invalidate();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const transitionStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/events/${id}`, { status: newStatus });
      toast.success(`Event ${newStatus.toLowerCase()}`);
      setForm(null); // reload
      invalidate();
    } catch (err) { toast.error(err.message); }
  };

  if (isLoading || !form) return <div className="py-12 text-center"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>;

  return (
    <div data-testid="event-edit-page">
      <Link to="/admin/events" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]">
        <ArrowLeft size={16} /> Events
      </Link>

      <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
        <div className="flex items-center gap-3">
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide" data-testid="event-edit-title">{event.title}</h1>
          <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 border ${statusStyle(event.status)}`} data-testid="event-status-badge">{event.status}</span>
        </div>
        <div className="flex gap-2">
          {event.status === "DRAFT" && (
            <button onClick={() => transitionStatus("PUBLISHED")} className="lv-btn-primary inline-flex items-center gap-2 text-xs min-h-[36px] px-4" data-testid="publish-event-btn">
              <Send size={14} /> Publish
            </button>
          )}
          {event.status === "PUBLISHED" && (
            <button onClick={() => transitionStatus("EXPIRED")} className="lv-btn-secondary inline-flex items-center gap-2 text-xs min-h-[36px] px-4" data-testid="expire-event-btn">
              <Archive size={14} /> Expire
            </button>
          )}
        </div>
      </div>

      <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
        <h2 className="text-xs tracking-[0.3em] uppercase text-lv-muted mb-3">Event Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Title</label><input value={form.title} onChange={(e) => update("title", e.target.value)} className="lv-input" data-testid="edit-event-title" /></div>
          <div className="sm:col-span-2"><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Description</label><textarea value={form.description} onChange={(e) => update("description", e.target.value)} className="lv-input min-h-[80px]" rows={3} /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Event Type</label>
            <Select value={form.event_type} onValueChange={(v) => update("event_type", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{EVENT_TYPES.map((t) => <SelectItem key={t} value={t} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Audience</label>
            <Select value={form.audience} onValueChange={(v) => update("audience", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{AUDIENCES.map((a) => <SelectItem key={a} value={a} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{a.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Date</label><input type="date" value={form.date} onChange={(e) => update("date", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Location</label><input value={form.location} onChange={(e) => update("location", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Start Time</label><input type="time" value={form.time_start} onChange={(e) => update("time_start", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">End Time</label><input type="time" value={form.time_end} onChange={(e) => update("time_end", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Chapter</label>
            <Select value={form.chapter_id} onValueChange={(v) => update("chapter_id", v)}>
              <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[44px]"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-walnut-deep border-lv-border">{chapters.map((ch) => <SelectItem key={ch.id} value={ch.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">{ch.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Capacity</label><input type="number" value={form.capacity} onChange={(e) => update("capacity", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Member Price</label><input value={form.member_price} onChange={(e) => update("member_price", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Image URL</label><input value={form.image_url} onChange={(e) => update("image_url", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Action URL</label><input value={form.action_url} onChange={(e) => update("action_url", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Action Label</label><input value={form.action_label} onChange={(e) => update("action_label", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Publish Date</label><input type="date" value={form.publish_date} onChange={(e) => update("publish_date", e.target.value)} className="lv-input" /></div>
          <div><label className="block text-[10px] tracking-[0.2em] uppercase text-lv-muted mb-1">Expiration Date</label><input type="date" value={form.expiration_date} onChange={(e) => update("expiration_date", e.target.value)} className="lv-input" /></div>
        </div>

        <div className="text-xs text-lv-muted/60 space-y-0.5">
          {event.created_at && <p>Created: {new Date(event.created_at).toLocaleDateString()}</p>}
          {event.updated_at && <p>Updated: {new Date(event.updated_at).toLocaleDateString()}</p>}
        </div>

        <button onClick={saveEvent} disabled={saving} className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="save-event-btn">
          <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
